import { Duration } from "luxon";

import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import { enumValueFor, fieldHasRole } from "@storyboard/dispatch-view/fieldKinds";
import type { DispatchControl, DispatchControlValue, DispatchField } from "@storyboard/dispatch-view/view";
import { fieldRefOf } from "@storyboard/dispatch-view/view";
import type { FieldRef, FilterNode, LeafNode } from "@storyboard/driver-query/predicate";
import { fieldKey } from "@storyboard/driver-query/predicate";

/**
 * Whether the row menu offers "Only" and "Exclude" on a field, and the rows can group by it: the registry's `group`
 * role, given to fields whose values repeat across rows in a way worth narrowing to. A timestamp, a number, a message
 * body, the coordinates, and the channel alias have no such role.
 */
export function canConditionOn(field: DispatchField): boolean {
  return fieldHasRole(field, "group");
}

/** What a values editor and the strip call the choice of rows whose field has no value. */
export const BLANK_LABEL = "(blank)";

/**
 * The field a condition is about: its own for a leaf, the leaf's under a `not`, and for an `or` the one field every
 * child is about (a values editor's "these values or (blank)"), else none. An `and` has none.
 */
export function conditionField(node: FilterNode): DispatchField | null {
  if ("not" in node) return conditionField(node.not);
  if ("or" in node) {
    const fields = new Set(node.or.map(conditionField));
    return fields.size === 1 ? ([...fields][0] ?? null) : null;
  }
  return "field" in node ? fieldKey(node.field) : null;
}

/** The ad-hoc conditions about one field, so a column's editor can show what is already in force. */
export function conditionsOnField(adhoc: readonly FilterNode[], field: DispatchField): FilterNode[] {
  return adhoc.filter((condition) => conditionField(condition) === field);
}

/** The positive leaves of a field's conditions, looking inside the `or` a values editor writes alongside (blank). */
function positiveLeaves(adhoc: readonly FilterNode[], field: DispatchField): FieldLeaf[] {
  return conditionsOnField(adhoc, field).flatMap((condition) => {
    if ("or" in condition) return condition.or.filter((child): child is FieldLeaf => "field" in child);
    if ("field" in condition) return [condition];
    return [];
  });
}

/**
 * The values the positive conditions on a field name: every `eq` and `inArray` leaf's, in order, without repeats.
 *
 * What a values editor opens on, and what the Filter menu's editor for a field with no column shows as chosen.
 */
export function chosenValues(adhoc: readonly FilterNode[], field: DispatchField): string[] {
  const chosen = new Set<string>();
  for (const leaf of positiveLeaves(adhoc, field)) {
    if ("inArray" in leaf) for (const value of leaf.inArray) chosen.add(value);
    if ("eq" in leaf) chosen.add(leaf.eq);
  }
  return [...chosen];
}

/** Whether the positive conditions on a field keep the rows where it has no value: what (blank) opens checked on. */
export function blankChosen(adhoc: readonly FilterNode[], field: DispatchField): boolean {
  return positiveLeaves(adhoc, field).some((leaf) => "isEmpty" in leaf);
}

/**
 * The conditions a values editor writes for its chosen values and (blank): one `inArray` leaf, one `isEmpty` leaf,
 * the `or` of the two when both are chosen, or none when nothing is.
 */
export function valuesCondition(field: DispatchField, values: readonly string[], blank = false): FilterNode[] {
  const ref = fieldRefOf(field);
  const leaves: FilterNode[] = [];
  if (values.length > 0) leaves.push({ field: ref, inArray: [...values] });
  if (blank) leaves.push({ field: ref, isEmpty: {} });
  return leaves.length === 2 ? [{ or: leaves }] : leaves;
}

/**
 * The ad-hoc conditions with every one about `field` replaced by `next`, in the place the first one held; an empty
 * `next` drops the field's conditions. Conditions on other fields are untouched.
 */
export function replaceFieldConditions(
  adhoc: readonly FilterNode[],
  field: DispatchField,
  next: readonly FilterNode[],
): FilterNode[] {
  const first = adhoc.findIndex((condition) => conditionField(condition) === field);
  const others = adhoc.filter((condition) => conditionField(condition) !== field);
  if (first === -1) return [...others, ...next];
  // The others before the first replaced condition keep their order; the replacements slot in where it was.
  const before = adhoc.slice(0, first).filter((condition) => others.includes(condition));
  const after = others.slice(before.length);
  return [...before, ...next, ...after];
}

/** A positive leaf's field kinds the header editor can pre-fill from: exact values, a pattern, a span, or a radius. */
export type FieldLeaf = Extract<LeafNode, { field: FieldRef }>;

/** A leaf keeping only rows where `field` equals `value`. */
export function onlyCondition(field: DispatchField, value: string): FilterNode {
  return { field: fieldRefOf(field), eq: value };
}

/** A leaf dropping the rows where `field` equals `value`. */
export function excludeCondition(field: DispatchField, value: string): FilterNode {
  return { not: { field: fieldRefOf(field), eq: value } };
}

/** What a chip says about a condition: a short summary for the strip and the full detail for its tooltip. */
export type ConditionText = { summary: string; detail: string };

/** How the strip names things it needs help with: a field's label and kind, and an enum value's label. */
export type ConditionNaming = {
  labelFor: (field: DispatchField) => string;
  kindFor: (field: DispatchField) => FieldKind;
};

const MINUTES_PER_DAY = Duration.fromObject({ days: 1 }).as("minutes");
const MINUTES_PER_HOUR = Duration.fromObject({ hours: 1 }).as("minutes");

function formatMinutes(minutes: number): string {
  if (minutes % MINUTES_PER_DAY === 0) return `${minutes / MINUTES_PER_DAY}d`;
  if (minutes % MINUTES_PER_HOUR === 0) return `${minutes / MINUTES_PER_HOUR}h`;
  return `${minutes}m`;
}

function formatRadius(spec: {
  radius: number;
  unit: "miles" | "km";
  label?: string;
  latitude: number;
  longitude: number;
}) {
  const distance = `${spec.radius} ${spec.unit === "miles" ? "mi" : "km"}`;
  const place = spec.label ?? `${spec.latitude.toFixed(4)}, ${spec.longitude.toFixed(4)}`;
  return { distance, place };
}

function valueLabel(naming: ConditionNaming, field: DispatchField, value: string): string {
  return enumValueFor(naming.kindFor(field), value)?.label ?? value;
}

/** Whether a field's values name its states on their own (a boolean such as Unacknowledged / Acknowledged). */
function standsAlone(naming: ConditionNaming, field: DispatchField): boolean {
  const kind = naming.kindFor(field);
  return kind.valueType === "boolean" && kind.values != null;
}

/**
 * Describes the `or` a values editor writes for values and (blank) the way an `inArray` reads, with (blank) among the
 * values; `null` for any other combination.
 */
function valuesWithBlank(children: readonly FilterNode[], naming: ConditionNaming): ConditionText | null {
  const [first] = children;
  if (!first || !("field" in first)) return null;
  const field = fieldKey(first.field);
  const values: string[] = [];
  for (const child of children) {
    if (!("field" in child) || fieldKey(child.field) !== field) return null;
    if ("inArray" in child) values.push(...child.inArray.map((value) => valueLabel(naming, field, value)));
    else if ("eq" in child) values.push(valueLabel(naming, field, child.eq));
    else if ("isEmpty" in child) values.push(BLANK_LABEL);
    else return null;
  }
  const label = naming.labelFor(field);
  const summary = values.length <= 2 ? `${label}: ${values.join(", ")}` : `${label} · ${values.length}`;
  return { summary, detail: `${label} is one of ${values.join(", ")}` };
}

/**
 * Describes one condition in the dispatcher's words: `Driver = Bo Barker`, `Region · 3` with the values behind it,
 * `Time older than 2h`, `Location within 50 mi of Fresno`, `Any of 3 conditions`, and a boolean state that names
 * itself as just that (`Acknowledged`). Negation reads as `≠`, `Not`, or `not`.
 */
export function describeCondition(node: FilterNode, naming: ConditionNaming): ConditionText {
  if ("not" in node) {
    const inner = node.not;
    if ("field" in inner && "eq" in inner) {
      const field = fieldKey(inner.field);
      const label = naming.labelFor(field);
      const value = valueLabel(naming, field, inner.eq);
      if (standsAlone(naming, field)) return { summary: `Not ${value}`, detail: `Not ${value}` };
      return { summary: `${label} ≠ ${value}`, detail: `${label} is not ${value}` };
    }
    const described = describeCondition(inner, naming);
    return { summary: `not ${described.summary}`, detail: `Not: ${described.detail}` };
  }
  if ("or" in node) {
    const chosen = valuesWithBlank(node.or, naming);
    if (chosen) return chosen;
  }
  if ("and" in node || "or" in node) {
    const children = "and" in node ? node.and : node.or;
    const word = "and" in node ? "All" : "Any";
    const details = children.map((child) => describeCondition(child, naming).detail);
    return {
      summary: `${word} of ${children.length} condition${children.length === 1 ? "" : "s"}`,
      detail: `${word} of: ${details.join("; ")}`,
    };
  }
  if ("isTrue" in node) return { summary: "Everything", detail: "Matches every row" };
  if ("isFalse" in node) return { summary: "Nothing", detail: "Matches no row" };
  const field = fieldKey(node.field);
  const label = naming.labelFor(field);
  if ("eq" in node) {
    const value = valueLabel(naming, field, node.eq);
    if (standsAlone(naming, field)) return { summary: value, detail: value };
    return { summary: `${label} = ${value}`, detail: `${label} is ${value}` };
  }
  if ("inArray" in node) {
    const values = node.inArray.map((value) => valueLabel(naming, field, value));
    const summary = values.length <= 2 ? `${label}: ${values.join(", ")}` : `${label} · ${values.length}`;
    return { summary, detail: `${label} is one of ${values.join(", ")}` };
  }
  if ("regex" in node) {
    return { summary: `${label} matches`, detail: `${label} matches /${node.regex.pattern}/${node.regex.flags ?? ""}` };
  }
  if ("isEmpty" in node) return { summary: `${label}: ${BLANK_LABEL}`, detail: `${label} has no value` };
  if ("olderThan" in node) {
    const span = formatMinutes(node.olderThan.minutes);
    return { summary: `${label} > ${span} ago`, detail: `${label} more than ${span} ago` };
  }
  if ("newerThan" in node) {
    const span = formatMinutes(node.newerThan.minutes);
    return { summary: `${label} last ${span}`, detail: `${label} within the last ${span}` };
  }
  if ("gt" in node) return { summary: `${label} > ${node.gt}`, detail: `${label} greater than ${node.gt}` };
  if ("lt" in node) return { summary: `${label} < ${node.lt}`, detail: `${label} less than ${node.lt}` };
  const { distance, place } = formatRadius(node.withinRadius);
  return { summary: `${label} within ${distance} of ${place}`, detail: `${label} within ${distance} of ${place}` };
}

/**
 * Describes an active control the way the strip shows it, or `null` when the control contributes nothing.
 */
export function describeControl(
  control: DispatchControl,
  value: DispatchControlValue | undefined,
  naming: ConditionNaming,
): ConditionText | null {
  switch (control.kind) {
    case "toggle":
      return value === true ? { summary: control.label, detail: `${control.label} is on` } : null;
    case "values": {
      const values = Array.isArray(value) ? value : [];
      if (values.length === 0) return null;
      const label = control.label ?? naming.labelFor(control.field);
      const labels = values.map((entry) => valueLabel(naming, control.field, entry));
      return {
        summary: labels.length <= 2 ? `${label}: ${labels.join(", ")}` : `${label} · ${labels.length}`,
        detail: `${label} is one of ${labels.join(", ")}`,
      };
    }
    case "time": {
      if (typeof value !== "number" || value <= 0) return null;
      const label = control.label ?? naming.labelFor(control.field);
      return {
        summary: `${label}: last ${formatMinutes(value)}`,
        detail: `${label} within the last ${formatMinutes(value)}`,
      };
    }
  }
}
